import { CONFIG } from '@h2h/shared';
import { BrandMark } from '../ui/bits';
import { tierShort } from '../../lib/format';

// The desktop-only "marketing" side of the pre-auth flow. None of this renders on
// mobile (the phone screens own the full surface there); it exists purely to turn the
// wide desktop window into a real two-pane app instead of a phone column floating in
// the void. Shared by the auth split layout (AuthPage) and the Welcome hero.

type LadderBar = { tier: number; h: number; you?: boolean; gold?: boolean };

const LADDER: LadderBar[] = [
  { tier: 0, h: 16 },
  { tier: 1, h: 26 },
  { tier: 2, h: 38, gold: true },
  { tier: 3, h: 54, you: true },
  { tier: 4, h: 68 },
  { tier: 5, h: 84 }
];

const SOCIAL_AVATARS = [
  { initials: 'MA', from: '#5B6EE1', to: '#33408F' },
  { initials: 'DT', from: '#E1885B', to: '#9F4A2A' },
  { initials: 'PS', from: '#5BE19A', to: '#2A9F66' }
] as const;

const RIVALS = CONFIG.DIVISION_SIZE - 1;

/** Avatars + "players competing" social-proof line, reused across the desktop panels. */
export function SocialProof({ tone = 'dark' }: { tone?: 'dark' | 'light' }) {
  return (
    <div className="flex items-center gap-[10px]">
      <div className="flex">
        {SOCIAL_AVATARS.map(({ initials, from, to }, i) => (
          <div
            key={initials}
            style={{
              backgroundImage: `linear-gradient(135deg, ${from}, ${to})`,
              marginLeft: i === 0 ? 0 : -8
            }}
            className={
              tone === 'dark'
                ? 'grid h-7 w-7 place-items-center rounded-full border-2 border-[#0C0C12] text-[10px] font-extrabold text-white'
                : 'grid h-7 w-7 place-items-center rounded-full border-2 border-white text-[10px] font-extrabold text-white'
            }
          >
            {initials}
          </div>
        ))}
      </div>
      <span
        className={
          tone === 'dark'
            ? 'text-[13px] font-semibold text-white/60'
            : 'text-[13px] font-semibold text-ink2'
        }
      >
        <span className={tone === 'dark' ? 'font-extrabold text-white' : 'font-extrabold text-ink'}>
          Free to play
        </span>{' '}
        · a new round every match-day
      </span>
    </div>
  );
}

/**
 * The signature tier-ladder card with two floating "live" chips. Designed to sit on a
 * dark surface (used inside the brand panel and the Welcome desktop hero).
 */
export function LadderShowcase() {
  return (
    <div className="relative w-full max-w-[330px]">
      <div className="relative rounded-[26px] border border-white/10 bg-white/[0.05] p-6 shadow-[0_44px_80px_-34px_rgba(0,0,0,0.85)] backdrop-blur-[6px]">
        <div className="mb-5 flex items-center justify-between">
          <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-[#9A9AA4]">
            Tier ladder
          </span>
          <span className="text-[12px] font-extrabold text-accent">▲ Promoted</span>
        </div>
        <div className="flex items-end justify-between gap-[6px]">
          {LADDER.map(({ tier, h, you, gold }) => (
            <div key={tier} className="relative flex-1 text-center">
              {you && (
                <span className="absolute -top-[17px] left-0 right-0 text-[10px] font-extrabold text-accent">
                  YOU
                </span>
              )}
              <div
                style={{ height: h }}
                className={
                  you
                    ? 'rounded-[7px] border-[1.5px] border-accent bg-gradient-to-b from-[#E6ECF5] to-[#9FB2CC] shadow-[0_0_24px_rgba(36,197,106,0.55)]'
                    : gold
                      ? 'rounded-[6px] bg-gradient-to-b from-gold-top to-gold-bottom opacity-45'
                      : 'rounded bg-white/12'
                }
              />
              <div
                className={
                  you
                    ? 'mt-[6px] text-[9px] font-extrabold text-[#C9D4E4]'
                    : 'mt-[6px] text-[9px] font-bold text-[#7E7E88]'
                }
              >
                {tierShort(tier)}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* floating live score chip */}
      <div className="absolute -left-5 top-7 flex animate-h2h-float items-center gap-2 rounded-[14px] border border-white/10 bg-[rgba(20,20,26,0.92)] px-3 py-[9px] shadow-[0_18px_34px_-16px_rgba(0,0,0,0.8)]">
        <span className="h-[7px] w-[7px] animate-h2h-pulse rounded-full bg-accent" />
        <span className="num text-[12px] font-extrabold tracking-snug text-white">ARS 2–1 TOT</span>
        <span className="num text-[10px] font-extrabold text-accent">71'</span>
      </div>

      {/* floating points chip */}
      <div className="absolute -bottom-6 -right-4 flex animate-h2h-float-slow flex-col items-end rounded-2xl border border-accent/40 bg-[rgba(20,20,26,0.92)] px-[15px] py-[12px] shadow-[0_18px_34px_-16px_rgba(0,0,0,0.8)]">
        <span className="text-[9px] font-bold uppercase tracking-wide text-[#9A9AA4]">
          This round
        </span>
        <span className="num mt-[2px] text-[28px] font-extrabold leading-none tracking-tight text-accent [text-shadow:0_0_24px_rgba(36,197,106,0.5)]">
          +235
        </span>
      </div>
    </div>
  );
}

/** Shared dark radial canvas + green glows used by every desktop pre-auth surface. */
export function BrandCanvas() {
  return (
    <>
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(125% 90% at 32% -10%, #16271F 0%, #11121A 46%, #08080C 100%)'
        }}
      />
      <div className="pointer-events-none absolute -left-24 top-[-130px] h-[380px] w-[380px] rounded-full bg-[radial-gradient(circle,rgba(36,197,106,0.24),transparent_64%)]" />
      <div className="pointer-events-none absolute -bottom-20 right-[-70px] h-[320px] w-[320px] rounded-full bg-[radial-gradient(circle,rgba(36,197,106,0.10),transparent_70%)]" />
    </>
  );
}

/**
 * Desktop-only left brand rail for the auth screens (log in, sign up, reset, profile,
 * onboarding). Carries the identity so the right-hand form reads as one half of a real
 * desktop app. Hidden entirely on mobile.
 */
export function AuthBrandPanel() {
  return (
    <aside className="relative hidden shrink-0 overflow-hidden text-white md:flex md:w-[42%] md:flex-col lg:w-[46%] xl:w-[44%]">
      <BrandCanvas />

      <div className="relative flex items-center gap-3 px-12 pt-12 lg:px-16 lg:pt-14">
        <BrandMark size={40} />
        <span className="text-[19px] font-extrabold tracking-tight">HeadToHead</span>
      </div>

      <div className="relative flex flex-1 items-center justify-center px-12 py-10 lg:px-16">
        <LadderShowcase />
      </div>

      <div className="relative px-12 pb-12 lg:px-16 lg:pb-14">
        <h2 className="title text-[26px] leading-[1.08] text-white">
          Call the games.
          <br />
          <span className="text-accent">Climb the divisions.</span>
        </h2>
        <p className="mt-3 max-w-[360px] text-[14px] font-medium leading-[1.5] text-white/55">
          Predict football and cycling every round, build your card and stack the boosts, and battle {RIVALS}{' '}
          rivals for promotion.
        </p>
        <div className="mt-7">
          <SocialProof tone="dark" />
        </div>
      </div>
    </aside>
  );
}
